import { SignJWT } from "jose";
import type { AuthHeaderProvider } from "@mydailydirect/api-client";
import {
  ALL_ROLES,
  SUPER_ADMIN_ROLES,
  isSuperAdminSession,
  type Session,
} from "./session";

export interface GatewayConfig {
  /** HMAC secret shared with the backend gateway-trust mode (dev/local only in the browser). */
  hmacSecret: string;
  issuer: string;
  audience: string;
}

/**
 * Reads gateway config from Vite env vars, defaulting to the backend's `local` profile values so the
 * stack works out-of-the-box with `docker compose up` or local boot.
 */
export function gatewayConfigFromEnv(env: Record<string, string | undefined>): GatewayConfig {
  const secret = (env.VITE_GATEWAY_HMAC_SECRET && env.VITE_GATEWAY_HMAC_SECRET.trim().length > 0)
    ? env.VITE_GATEWAY_HMAC_SECRET.trim()
    : "change-me-32-byte-minimum-trado-secret-key-2026!!";

  const issuer = (env.VITE_GATEWAY_ISSUER && env.VITE_GATEWAY_ISSUER.trim().length > 0)
    ? env.VITE_GATEWAY_ISSUER.trim()
    : "https://mydailydirect.in";

  const audience = (env.VITE_GATEWAY_AUDIENCE && env.VITE_GATEWAY_AUDIENCE.trim().length > 0)
    ? env.VITE_GATEWAY_AUDIENCE.trim()
    : "mydailydirect";

  return {
    hmacSecret: secret,
    issuer: issuer,
    audience: audience,
  };
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Mints a fresh, single-use signed assertion for one request. A new `jti` each call satisfies the
 * backend's replay protection, so the token cannot be cached across requests.
 */
export async function mintAssertion(session: Session, config: GatewayConfig): Promise<string> {
  const key = new TextEncoder().encode(config.hmacSecret);
  const assertedRoles = isSuperAdminSession(session)
    ? [...SUPER_ADMIN_ROLES]
    : session.roles?.includes("ADMIN")
      ? [...ALL_ROLES]
      : session.roles;
  const phone = session.phone || session.phoneNumber || "";
  const orgId = session.orgId || session.organizationId || session.tenantId || "mydailydirect";

  return new SignJWT({
    userId: session.userId,
    tenantId: session.tenantId || "mydailydirect",
    roles: assertedRoles,
    phone: phone,
    phoneNumber: phone,
    orgId: orgId,
    organizationId: orgId,
  })
    .setProtectedHeader({ alg: "HS256" })
    .setSubject(session.userId)
    .setIssuer(config.issuer)
    .setAudience(config.audience)
    .setJti(randomId())
    .setIssuedAt()
    .setExpirationTime("5m")
    .sign(key);
}

/**
 * Builds an {@link AuthHeaderProvider} that mints a fresh assertion (and correlation id) for every
 * request from the current session. Returns empty headers when signed out so requests fail cleanly.
 */
export function createAuthHeaderProvider(
  getSession: () => Session | null,
  config: GatewayConfig,
): AuthHeaderProvider {
  return async (): Promise<Record<string, string>> => {
    const session: Session = getSession() ?? {
      tenantId: "mydailydirect",
      userId: "system.bootstrap",
      fullName: "System Bootstrap",
      roles: ["RETAILER"],
      phone: "",
      phoneNumber: "",
      orgId: "mydailydirect",
      organizationId: "mydailydirect",
    };
    const token = await mintAssertion(session, config);
    return {
      "X-Gateway-Assertion": token,
      "X-Correlation-Id": `web-${randomId()}`,
      ...(session.orgId ? { "X-Org-Id": session.orgId } : {}),
      ...(session.phone ? { "X-User-Phone": session.phone } : {}),
    };
  };
}
